import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Eye, Star, Home, Clock, Droplets,
  FlaskConical, Scan, Wind, Microscope, X, AlertCircle,
  Upload, Download, CheckCircle2, XCircle as XCircleIcon, AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusToggleCell, ToggleSwitch } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import api, { labTestService, labTestCategoryService, timeSlotService } from '../services/api';
import { formatCurrency } from '../utils/helpers';

/* ── Sample type config ─────────────────────────────────────────────────────── */
const SAMPLE_TYPES = [
  { value: 'Blood',   label: 'Blood',   icon: Droplets,   color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20'    },
  { value: 'Urine',   label: 'Urine',   icon: FlaskConical,color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
  { value: 'Saliva',  label: 'Saliva',  icon: Wind,        color: 'text-blue-500',   bg: 'bg-blue-50 dark:bg-blue-900/20'  },
  { value: 'Imaging', label: 'Imaging', icon: Scan,        color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
  { value: 'Other',   label: 'Other',   icon: Microscope,  color: 'text-gray-500',   bg: 'bg-gray-50 dark:bg-gray-700/50'  },
];

const REPORT_TIMES = ['1 hr', '2 hrs', '4 hrs', '6 hrs', '12 hrs', '24 hrs', '48 hrs', '3 days', '5 days', '7 days'];

export const getSampleMeta = (type) => SAMPLE_TYPES.find(s => s.value === type) || SAMPLE_TYPES[4];

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', category: '', sampleType: '', status: '', priceMin: '', priceMax: '' };

const labTestFilterFn = (t, f) => {
  if (f.search && !t.name.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.category && t.category !== f.category) return false;
  // if (f.sampleType && t.sampleType !== f.sampleType) return false;
  if (f.status && t.status !== f.status) return false;
  if (f.priceMin && t.price < parseFloat(f.priceMin)) return false;
  if (f.priceMax && t.price > parseFloat(f.priceMax)) return false;
  return true;
};

/* ── Bulk Upload Modal ──────────────────────────────────────────────────────── */
const CSV_HEADERS = ['id','category_id','name','description','price','discounted_price','discount_percent','home_collection','sort_order','is_active'];

const parseLabCSV = (text) => {
  const lines = text.trim().split('\n').filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  return lines.slice(1).map((line, idx) => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row  = {};
    headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    const errors = [];
    if (!row.name)                                          errors.push('Name required');
    if (!row.price || isNaN(+row.price) || +row.price < 0) errors.push('Invalid price');
    return {
      _row:    idx + 2,
      _errors: errors,
      _valid:  errors.length === 0,
      _mode:   row.id ? 'update' : 'insert',
      id:               row.id               || '',
      category_id:      row.category_id      || '',
      name:             row.name             || '',
      description:      row.description      || '',
      price:            row.price            || '',
      discounted_price: row.discounted_price || '',
      discount_percent: row.discount_percent || '',
      home_collection:  row.home_collection  || '',
      sort_order:       row.sort_order       || '',
      is_active:        row.is_active        || '',
    };
  });
};

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const fileRef               = useRef();
  const [step, setStep]       = useState(1);
  const [file, setFile]       = useState(null);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error('Please upload a .csv file'); return; }
    setFile(f); setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseLabCSV(ev.target.result);
      if (!parsed.length) { toast.error('No data rows found in CSV'); return; }
      setRows(parsed); setStep(2);
    };
    reader.readAsText(f);
  };

  const validRows   = rows.filter(r => r._valid);
  const invalidRows = rows.filter(r => !r._valid);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await labTestService.bulkUpload(file);
      toast.success(res?.message || 'Lab tests imported successfully!');
      setStep(3); onSuccess();
    } catch (err) { toast.error(err?.response?.data?.message || 'Import failed'); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Bulk Upload Lab Tests" size="xl">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {['Upload CSV', 'Preview & Validate', 'Done'].map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-medihub-primary text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}>{step > i + 1 ? '✓' : i + 1}</div>
              <span className={`text-xs font-medium ${step === i + 1 ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>{label}</span>
            </div>
            {i < 2 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <Download className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Step 1: Download the CSV template</p>
              <p className="text-xs text-gray-500 mt-0.5">Template mein existing lab tests bhi filled honge.</p>
              <button onClick={() => labTestService.downloadTemplate().catch(() => toast.error('Failed to download'))}
                className="mt-2 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                <Download className="w-3.5 h-3.5" />Download Template
              </button>
            </div>
          </div>
          <div onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center cursor-pointer hover:border-medihub-primary transition-colors">
            <Upload className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Click to upload CSV file</p>
            <p className="text-xs text-gray-400 mt-1">Only .csv files supported</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">CSV columns:</p>
            <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{CSV_HEADERS.join(', ')}</p>
            <p className="text-xs text-gray-400 mt-1">💡 id blank = new insert &nbsp;|&nbsp; id filled = existing update</p>
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl text-center">
              <p className="text-xl font-bold text-gray-900 dark:text-white">{rows.length}</p>
              <p className="text-xs text-gray-500">Total Rows</p>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl text-center">
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{validRows.length}</p>
              <p className="text-xs text-gray-500">Valid</p>
            </div>
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-center">
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{invalidRows.length}</p>
              <p className="text-xs text-gray-500">Errors</p>
            </div>
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr>
                  {['Row', 'Mode', 'ID', 'Name', 'Category', 'Price', 'Disc%', 'Home', 'Active', 'Errors'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(row => (
                  <tr key={row._row} className={row._valid ? '' : 'bg-red-50 dark:bg-red-900/10'}>
                    <td className="px-3 py-2 font-mono">
                      <div className="flex items-center gap-1">
                        {row._valid ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <XCircleIcon className="w-3.5 h-3.5 text-red-500" />}
                        {row._row}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        row._mode === 'update' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>{row._mode}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-500">{row.id || '—'}</td>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{row.name || <span className="text-red-400">—</span>}</td>
                    <td className="px-3 py-2">{row.category_id || '—'}</td>
                    <td className="px-3 py-2">{row.price}</td>
                    <td className="px-3 py-2">{row.discount_percent || '—'}</td>
                    <td className="px-3 py-2">{row.home_collection || '—'}</td>
                    <td className="px-3 py-2">{row.is_active || '—'}</td>
                    <td className="px-3 py-2">
                      {row._valid
                        ? <span className="text-green-600">OK</span>
                        : <div className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-red-500 shrink-0 mt-0.5" /><span className="text-red-500">{row._errors.join('; ')}</span></div>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between gap-3 pt-1">
            <button onClick={() => { setStep(1); setRows([]); setFile(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; }} className="btn-secondary">← Re-upload</button>
            <button onClick={handleImport} disabled={loading || !file}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              <Upload className="w-4 h-4" />{loading ? 'Importing…' : `Import ${fileName}`}
            </button>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">Import Successful!</p>
            {invalidRows.length > 0 && <p className="text-sm text-orange-500 mt-1">{invalidRows.length} row(s) skipped due to errors.</p>}
          </div>
          <button onClick={onClose} className="btn-primary">Done</button>
        </div>
      )}
    </Modal>
  );
};

/* ── Slot Manager Modal ─────────────────────────────────────────────── */
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const SlotManagerModal = ({ test, onClose }) => {
  const [slots, setSlots] = useState(
    DAYS.map(day => ({ day, active: false, times: [] }))
  );
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    labTestService.getSlots(test.id)
      .then(existing => {
        // existing is object like { tuesday: [{id, start_time, end_time}], ... }
        if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
          setSlots(DAYS.map(day => {
            const daySlots = existing[day] || [];
            return {
              day,
              active: daySlots.length > 0,
              times: daySlots.map(s => ({
                start_time: s.start_time?.slice(0, 5),
                end_time:   s.end_time?.slice(0, 5),
              })),
            };
          }));
        } else if (Array.isArray(existing) && existing.length) {
          setSlots(DAYS.map(day => {
            const daySlots = existing.filter(s => s.day_of_week === day);
            return {
              day,
              active: daySlots.length > 0,
              times: daySlots.map(s => ({
                start_time: s.start_time?.slice(0, 5),
                end_time:   s.end_time?.slice(0, 5),
              })),
            };
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [test.id]);

  const toggleDay = (day) => setSlots(prev => prev.map(d =>
    d.day === day ? { ...d, active: !d.active, times: d.active ? [] : [{ start_time: '09:00', end_time: '10:00' }] } : d
  ));

  const addTime = (day) => setSlots(prev => prev.map(d =>
    d.day === day ? { ...d, times: [...d.times, { start_time: '09:00', end_time: '10:00' }] } : d
  ));

  const removeTime = (day, idx) => setSlots(prev => prev.map(d =>
    d.day === day ? { ...d, times: d.times.filter((_, i) => i !== idx) } : d
  ));

  const updateTime = (day, idx, field, value) => setSlots(prev => prev.map(d =>
    d.day === day ? { ...d, times: d.times.map((t, i) => i === idx ? { ...t, [field]: value } : t) } : d
  ));

  const handleSave = async () => {
    const payload = slots
      .filter(d => d.active)
      .flatMap(d => d.times.map(t => ({ day_of_week: d.day, start_time: t.start_time, end_time: t.end_time })));
    setSaving(true);
    try {
      const res = await labTestService.saveSlots(test.id, payload);
      toast.success(res?.message || 'Slots saved!');
      onClose();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to save slots'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Slots — ${test.name}`} size="lg">
      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="h-14 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {slots.map(({ day, active, times }) => (
            <div key={day} className={`border rounded-xl p-4 transition-colors ${
              active ? 'border-medihub-primary dark:border-medihub-accent bg-medihub-light/30 dark:bg-medihub-dark/10' : 'border-gray-200 dark:border-gray-700'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => toggleDay(day)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      active ? 'bg-medihub-primary' : 'bg-gray-300 dark:bg-gray-600'
                    }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      active ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{day}</span>
                  {!active && <span className="text-xs text-gray-400">Unavailable</span>}
                </div>
                {active && (
                  <button type="button" onClick={() => addTime(day)}
                    className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-medihub-primary text-white hover:bg-medihub-secondary transition-colors">
                    <Plus className="w-3 h-3" />Add Slot
                  </button>
                )}
              </div>
              {active && (
                <div className="space-y-2">
                  {times.length === 0 && <p className="text-xs text-gray-400 italic">No slots — click "Add Slot"</p>}
                  {times.map((t, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input type="time" value={t.start_time}
                        onChange={e => updateTime(day, idx, 'start_time', e.target.value)}
                        className="input-field flex-1 text-sm" />
                      <span className="text-xs text-gray-400">to</span>
                      <input type="time" value={t.end_time}
                        onChange={e => updateTime(day, idx, 'end_time', e.target.value)}
                        className="input-field flex-1 text-sm" />
                      <button type="button" onClick={() => removeTime(day, idx)}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-4">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving || loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save Slots'}
        </button>
      </div>
    </Modal>
  );
};

/* ── Lab Test Form ──────────────────────────────────────────────────────────── */
const LabTestForm = ({ initial, activeCategories, activeSlots, allTests, onSubmit, onClose }) => {
  const rules = {
    name:       { required: true },
    categoryId: { required: true },
    price:      { required: true },
    // sampleType: { required: true },
    // reportTime: { required: true },
  };

  const { values, errors, handleChange, handleBlur, handleSubmit, setValues } = useForm(
    initial ? {
      name:           initial.name || '',
      categoryId:     String(initial.category_id || initial.categoryId || ''),
      price:          String(initial.price || ''),
      sampleType:     initial.sampleType || '',
      sampleQuantity: String(initial.sampleQuantity || ''),
      reportTime:     initial.reportTime || '',
      description:    initial.description || '',
      fastingRequired: initial.fastingRequired || false,
      homeCollection:  initial.home_collection === 1 || initial.homeCollection || false,
      status:          initial.is_active === 1 ? 'active' : (initial.status || 'active'),
      featured:        initial.featured || false,
      discount:        String(initial.discount_percent || initial.discount || 0),
      timeSlots:       initial.timeSlots || [],
      packageTests:    initial.packageTests || [],
      image:           null,
    } : {
      name: '', categoryId: '', price: '', sampleType: '', sampleQuantity: '', reportTime: '',
      description: '', fastingRequired: false, homeCollection: false,
      status: 'active', featured: false, discount: '0',
      timeSlots: [], packageTests: [], image: null,
    },
    rules
  );

  // Auto-fill sample type & quantity from category's first sampleMapping
  useEffect(() => {
    if (!values.categoryId) return;
    const cat = activeCategories.find(c => c.id === parseInt(values.categoryId));
    if (!cat?.sampleMappings?.length) return;
    const mapping = cat.sampleMappings[0];
    setValues(p => ({ ...p, sampleType: mapping.sampleType, sampleQuantity: String(mapping.quantity) }));
  }, [values.categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSlot = (label) => {
    const cur = values.timeSlots || [];
    setValues(p => ({ ...p, timeSlots: cur.includes(label) ? cur.filter(s => s !== label) : [...cur, label] }));
  };

  const togglePackageTest = (id) => {
    const cur = values.packageTests || [];
    setValues(p => ({ ...p, packageTests: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] }));
  };

  const handleFormSubmit = (vals) => {
    if (parseFloat(vals.price) <= 0) { toast.error('Price must be positive'); return; }
    const cat = activeCategories.find(c => c.id === parseInt(vals.categoryId));
    const price    = parseFloat(vals.price);
    const discount = parseFloat(vals.discount || 0);
    const discounted_price = +(price - (price * discount / 100)).toFixed(2);
    onSubmit({
      ...vals,
      categoryId: parseInt(vals.categoryId),
      category: cat?.name || "",
      price,
      discount,
      discounted_price,
      sampleQuantity: vals.sampleQuantity
        ? parseFloat(vals.sampleQuantity)
        : undefined,
      sampleUnit: cat?.sampleMappings?.[0]?.unit || "ml",
      timeSlots: values.timeSlots,
      packageTests: values.packageTests,
      image: values.image,
      home_collection: vals.homeCollection ? 1 : 0,
    });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Name */}
        <div className="sm:col-span-2">
          <FormField label="Test Name" error={errors.name} required>
            <input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="e.g. Complete Blood Count (CBC)" />
          </FormField>
        </div>

        {/* Category */}
        <FormField label="Category" error={errors.categoryId} required>
          <select name="categoryId" value={values.categoryId} onChange={handleChange} onBlur={handleBlur} className="input-field">
            <option value="">Select category</option>
            {activeCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>

        {/* Sample Type */}
        {/* <FormField label="Sample Type" error={errors.sampleType} required>
          <select name="sampleType" value={values.sampleType} onChange={handleChange} onBlur={handleBlur} className="input-field">
            <option value="">Select sample type</option>
            {SAMPLE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </FormField> */}

        {/* Sample Quantity */}
        {/* <FormField label="Sample Quantity">
          <div className="flex gap-2">
            <input
              name="sampleQuantity" type="number" step="0.1" min="0"
              value={values.sampleQuantity} onChange={handleChange}
              className="input-field flex-1" placeholder="e.g. 5"
            />
            <span className="flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg">
              {activeCategories.find(c => c.id === parseInt(values.categoryId))?.sampleMappings?.[0]?.unit || 'ml'}
            </span>
          </div>
        </FormField> */}

        {/* Price */}
        <FormField label="Price" error={errors.price} required>
          <input name="price" type="number" step="0.01" min="0" value={values.price} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="25.00" />
        </FormField>

        {/* Discount */}
        <FormField label="Discount (%)">
          <input name="discount" type="number" min="0" max="100" value={values.discount} onChange={handleChange} className="input-field" placeholder="0" />
        </FormField>

        {/* Discounted Price Preview */}
        {values.price && (
          <div className="sm:col-span-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Discounted Price</span>
            <span className="font-bold text-green-600 dark:text-green-400 text-lg">
              {formatCurrency(
                parseFloat(values.price) - (parseFloat(values.price) * parseFloat(values.discount || 0) / 100)
              )}
            </span>
          </div>
        )}

        {/* Report Time */}
        {/* <FormField label="Report Delivery Time" error={errors.reportTime} required>
          <select name="reportTime" value={values.reportTime} onChange={handleChange} onBlur={handleBlur} className="input-field">
            <option value="">Select time</option>
            {REPORT_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </FormField> */}

        {/* Status */}
        <FormField label="Status">
          <select name="status" value={values.status} onChange={handleChange} className="input-field">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
      </div>

      {/* Description */}
      {/* <FormField label="Description">
        <textarea name="description" value={values.description} onChange={handleChange} className="input-field resize-none" rows={2} placeholder="Brief description of this test…" />
      </FormField> */}

      {/* Image */}
      <FormField label="Image">
        <input type="file" accept="image/*" className="input-field"
          onChange={e => setValues(p => ({ ...p, image: e.target.files[0] || null }))} />
      </FormField>

      {/* Toggles row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          // { key: 'fastingRequired', label: 'Fasting Required',       icon: AlertCircle, color: 'text-orange-500' },
          { key: 'homeCollection',  label: 'Home Collection',         icon: Home,        color: 'text-green-600'  },
          // { key: 'featured',        label: 'Featured / Popular',      icon: Star,        color: 'text-yellow-500' },
        ].map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${color}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            </div>
            <ToggleSwitch checked={!!values[key]} onChange={v => setValues(p => ({ ...p, [key]: v }))} label={label} size="sm" />
          </div>
        ))}
      </div>

      {/* Time Slots */}
      {/* {activeSlots.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Available Time Slots</label>
          <div className="flex flex-wrap gap-2">
            {activeSlots.map(slot => (
              <button
                key={slot.label}
                type="button"
                onClick={() => toggleSlot(slot.label)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border ${
                  (values.timeSlots || []).includes(slot.label)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                }`}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>
      )} */}

      {/* Package Tests */}
      {/* {allTests.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Package Tests <span className="text-xs text-gray-400 font-normal ml-1">(combine multiple tests)</span>
          </label>
          <div className="flex flex-wrap gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/40 min-h-[44px]">
            {(values.packageTests || []).map(id => {
              const t = allTests.find(x => x.id === id);
              return t ? (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium">
                  {t.name}
                  <button type="button" onClick={() => togglePackageTest(id)} className="hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                </span>
              ) : null;
            })}
            <select
              className="flex-1 min-w-[160px] text-sm bg-transparent outline-none text-gray-500 dark:text-gray-400"
              value=""
              onChange={e => { if (e.target.value) togglePackageTest(parseInt(e.target.value)); }}
            >
              <option value="">+ Add test to package</option>
              {allTests
                .filter(t => !initial || t.id !== initial.id)
                .filter(t => !(values.packageTests || []).includes(t.id))
                .map(t => <option key={t.id} value={t.id}>{t.name}</option>)
              }
            </select>
          </div>
        </div>
      )} */}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update Test' : 'Add Lab Test'}</button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
// const LabTests = () => {
//   const navigate = useNavigate();
//   const { data: tests, loading, refetch } = useFetch(() => labTestService.getAll());
//   const { data: activeCats }  = useFetch(() => labTestCategoryService.getActive());
//   const { data: activeSlots } = useFetch(() => timeSlotService.getActive());
//   const [modal, setModal] = useState(null);
//   const [selected, setSelected] = useState(null);
//   const [slotTarget, setSlotTarget] = useState(null);
//   const [showBulk, setShowBulk] = useState(false);

//   const filterFnCb = useCallback(labTestFilterFn, []);

//   // Build dynamic filter fields using active categories
//   const filterFields = [
//     {
//       key: 'category', label: 'Category', type: 'select',
//       options: (activeCats || []).map(c => ({ value: c.name, label: c.name })),
//     },
//     // {
//     //   key: 'sampleType', label: 'Sample Type', type: 'select',
//     //   options: SAMPLE_TYPES.map(s => ({ value: s.value, label: s.label })),
//     // },
//     {
//       key: 'status', label: 'Status', type: 'select',
//       options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
//     },
//   ];

//   const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, tests);

//   const handleSave = async (vals) => {
//     try {
//       if (modal === 'edit') {
//         const res = await labTestService.update(selected.id, vals);
//         toast.success(res?.message || 'Lab test updated!');
//       } else {
//         const res = await labTestService.create(vals);
//         toast.success(res?.message || 'Lab test added!');
//       }
//       refetch(); setModal(null); setSelected(null);
//     } catch (err) { toast.error(err?.response?.data?.message || 'Something went wrong'); }
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm('Delete this lab test?')) return;
//     try { await labTestService.delete(id); toast.success('Deleted'); refetch(); }
//     catch { toast.error('Failed to delete'); }
//   };

//   const handleToggleStatus = async (test) => {
//     try {
//       const res = await labTestService.toggleStatus(test.id, test.is_active);
//       toast.success(res?.message || 'Status updated');
//       refetch();
//     } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
//   };

//   const handleToggleFeatured = async (test) => {
//     try { await labTestService.toggleFeatured(test.id); toast.success(test.featured ? 'Removed from featured' : 'Marked as featured'); refetch(); }
//     catch { toast.error('Failed'); }
//   };

//   /* ── Columns ── */
//   const columns = [
//     {
//       key: 'name', label: 'Test Name', render: (v, row) => {
//         const meta = getSampleMeta(row.sampleType);
//         const Icon = meta.icon;
//         return (
//           <div className="flex items-center gap-3">
//             {row.image_url
//               ? <img src={row.image_url} alt={v} className="w-9 h-9 rounded-xl object-cover shrink-0" />
//               : <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}><Icon className={`w-4 h-4 ${meta.color}`} /></div>
//             }
//             <div>
//               <div className="flex items-center gap-1.5">
//                 <p className="font-medium text-gray-900 dark:text-white">{v}</p>
//                 {row.featured && <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />}
//               </div>
//               <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{row.category_name || row.category}</p>
//             </div>
//           </div>
//         );
//       }
//     },
//     // {
//     //   key: 'sampleType', label: 'Sample', render: (v) => {
//     //     const meta = getSampleMeta(v);
//     //     return (
//     //       <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${meta.bg} ${meta.color}`}>
//     //         <meta.icon className="w-3.5 h-3.5" />{v}
//     //       </span>
//     //     );
//     //   }
//     // },
//     {
//       key: 'price', label: 'Price', render: (v, row) => (
//         <div>
//           <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(v)}</p>
//           {row.discount_percent > 0 && (
//             <p className="text-xs text-green-600 dark:text-green-400">{row.discount_percent}% off → {formatCurrency(row.discounted_price)}</p>
//           )}
//         </div>
//       )
//     },
//     // { key: 'reportTime', label: 'Report Time', render: v => <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400"><Clock className="w-3.5 h-3.5" />{v}</span> },
//     {
//       key: 'home_collection', label: 'Badges', render: (v, row) => (
//         <div className="flex flex-col gap-1">
//           {v === 1 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><Home className="w-3 h-3" />Home</span>}
//         </div>
//       )
//     },
//     {
//       key: 'is_active', label: 'Status', render: (v, row) => (
//         <StatusToggleCell active={v === 1 || v === true} onToggle={() => handleToggleStatus(row)} />
//       )
//     },
//     {
//       key: 'id', label: 'Actions', render: (_, row) => (
//         <div className="flex items-center gap-1">
//           {/* <button onClick={() => navigate(`/lab-tests/${row.id}`)} className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-blue-500" title="View Details">
//             <Eye className="w-4 h-4" />
//           </button> */}
//           {/* <button onClick={() => setSlotTarget(row)} className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors text-indigo-500" title="Manage Slots">
//             <Clock className="w-4 h-4" />
//           </button> */}
//           {/* <button onClick={() => handleToggleFeatured(row)} className={`p-1.5 rounded-lg transition-colors ${row.featured ? 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`} title={row.featured ? 'Remove featured' : 'Mark featured'}>
//             <Star className={`w-4 h-4 ${row.featured ? 'fill-yellow-400' : ''}`} />
//           </button> */}
//           <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
//             <Edit2 className="w-4 h-4" />
//           </button>
//           <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
//             <Trash2 className="w-4 h-4" />
//           </button>
//         </div>
//       )
//     },
//   ];

//   /* Stats */
//   const activeTests   = tests?.filter(t => t.is_active === 1).length || 0;
//   const featuredTests = tests?.filter(t => t.featured).length || 0;
//   const homeTests     = tests?.filter(t => t.home_collection === 1).length || 0;

//   return (
//     <div className="space-y-4">
//       {/* Header */}
//       <div className="flex items-start justify-between flex-wrap gap-3">
//         <div>
//           <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lab Tests</h1>
//           <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage diagnostic lab tests and packages</p>
//         </div>
//         <div className="flex items-center gap-2">
//           <button onClick={() => setShowBulk(true)} className="btn-secondary flex items-center gap-1.5">
//             <Upload className="w-4 h-4" />Bulk Upload
//           </button>
//           <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
//             <Plus className="w-4 h-4" />Add Lab Test
//           </button>
//         </div>
//       </div>

//       {/* Stats cards */}
//       <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
//         {[
//           { label: 'Total Tests',    value: tests?.length || 0,  color: 'text-gray-900 dark:text-white',          bg: '' },
//           { label: 'Active',         value: activeTests,           color: 'text-green-700 dark:text-green-400',     bg: 'bg-green-50 dark:bg-green-900/10' },
//           { label: 'Featured',       value: featuredTests,         color: 'text-yellow-600 dark:text-yellow-400',   bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
//           { label: 'Home Collection',value: homeTests,             color: 'text-blue-600 dark:text-blue-400',       bg: 'bg-blue-50 dark:bg-blue-900/10' },
//         ].map(({ label, value, color, bg }) => (
//           <div key={label} className={`card p-4 text-center ${bg}`}>
//             <p className={`text-2xl font-bold ${color}`}>{value}</p>
//             <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
//           </div>
//         ))}
//       </div>

//       {/* Price range filter — inline above FilterBar */}
//       <div className="card p-3 flex flex-wrap items-center gap-3">
//         <span className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">Price Range:</span>
//         <div className="flex items-center gap-2 flex-1 min-w-[200px]">
//           <input
//             type="number" min="0" placeholder="Min"
//             value={filters.priceMin}
//             onChange={e => onFilterChange('priceMin', e.target.value)}
//             className="input-field py-1.5 text-sm w-24"
//           />
//           <span className="text-gray-400">—</span>
//           <input
//             type="number" min="0" placeholder="Max"
//             value={filters.priceMax}
//             onChange={e => onFilterChange('priceMax', e.target.value)}
//             className="input-field py-1.5 text-sm w-24"
//           />
//           {(filters.priceMin || filters.priceMax) && (
//             <button onClick={() => { onFilterChange('priceMin', ''); onFilterChange('priceMax', ''); }} className="text-xs text-red-500 hover:underline">Clear</button>
//           )}
//         </div>
//       </div>

//       {/* FilterBar */}
//       <FilterBar
//         filters={filters}
//         onChange={onFilterChange}
//         onReset={onReset}
//         searchPlaceholder="Search by test name…"
//         fields={filterFields}
//         activeCount={activeCount}
//       />

//       {/* Table */}
//       <Table
//         title={`Lab Tests (${filtered.length}${activeCount ? ` of ${tests?.length || 0}` : ''})`}
//         columns={columns}
//         data={filtered}
//         loading={loading}
//         exportFilename="lab-tests"
//       />

//       {showBulk && (
//         <BulkUploadModal onClose={() => setShowBulk(false)} onSuccess={() => { refetch(); setShowBulk(false); }} />
//       )}

//       {/* Slot Manager Modal */}
//       {slotTarget && (
//         <SlotManagerModal test={slotTarget} onClose={() => setSlotTarget(null)} />
//       )}

//       {/* Add / Edit Modal */}
//       <Modal
//         isOpen={modal === 'add' || modal === 'edit'}
//         onClose={() => { setModal(null); setSelected(null); }}
//         title={modal === 'edit' ? 'Edit Lab Test' : 'Add Lab Test'}
//         size="xl"
//       >
//         <LabTestForm
//           initial={modal === 'edit' ? selected : null}
//           activeCategories={activeCats || []}
//           activeSlots={activeSlots || []}
//           allTests={(tests || []).filter(t => !selected || t.id !== selected.id)}
//           onSubmit={handleSave}
//           onClose={() => { setModal(null); setSelected(null); }}
//         />
//       </Modal>
//     </div>
//   );
// };

// export default LabTests;


const LabTests = () => {
  const navigate = useNavigate();
  const { data: activeCats } = useFetch(() =>
    labTestCategoryService.getActive(),
  );
  const { data: activeSlots } = useFetch(() => timeSlotService.getActive());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [slotTarget, setSlotTarget] = useState(null);
  const [showBulk, setShowBulk] = useState(false);

  // ✅ CHANGE 1: useFetch hata diya, manual state add kiya
  const [tests, setTests] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  // ✅ CHANGE 2: fetchTests function
  const fetchTests = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await api.get("lab-tests/admin", {
        params: { page: pageNum, limit: LIMIT },
      });
      const data = res.data?.data;
      setTests(data?.tests || []);
      setPagination(data?.pagination || null);
    } catch {
      toast.error("Failed to load tests");
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ CHANGE 3: page change hone pe fetch
  useEffect(() => {
    fetchTests(page);
  }, [page]);

  const refetch = () => fetchTests(page);

  const filterFnCb = useCallback(labTestFilterFn, []);

  const filterFields = [
    {
      key: "category",
      label: "Category",
      type: "select",
      options: (activeCats || []).map((c) => ({
        value: c.name,
        label: c.name,
      })),
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
    },
  ];

  const {
    filters,
    onChange: onFilterChange,
    onReset,
    filtered,
    activeCount,
  } = useFilters(FILTER_INITIAL, filterFnCb, tests);

  // ✅ CHANGE 4: filter change hone pe page 1 pe reset
  const handleFilterChange = (key, value) => {
    setPage(1);
    onFilterChange(key, value);
  };

  const handleReset = () => {
    setPage(1);
    onReset();
  };

  const handleSave = async (vals) => {
    try {
      if (modal === "edit") {
        const res = await labTestService.update(selected.id, vals);
        toast.success(res?.message || "Lab test updated!");
      } else {
        const res = await labTestService.create(vals);
        toast.success(res?.message || "Lab test added!");
      }
      refetch();
      setModal(null);
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lab test?")) return;
    try {
      await labTestService.delete(id);
      toast.success("Deleted");
      refetch();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleToggleStatus = async (test) => {
    try {
      const res = await labTestService.toggleStatus(test.id, test.is_active);
      toast.success(res?.message || "Status updated");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  const handleToggleFeatured = async (test) => {
    try {
      await labTestService.toggleFeatured(test.id);
      toast.success(
        test.featured ? "Removed from featured" : "Marked as featured",
      );
      refetch();
    } catch {
      toast.error("Failed");
    }
  };

  /* ── Columns ── */
  const columns = [
    {
      key: "name",
      label: "Test Name",
      render: (v, row) => {
        const meta = getSampleMeta(row.sampleType);
        const Icon = meta.icon;
        return (
          <div className="flex items-center gap-3">
            {row.image_url ? (
              <img
                src={row.image_url}
                alt={v}
                className="w-9 h-9 rounded-xl object-cover shrink-0"
              />
            ) : (
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}
              >
                <Icon className={`w-4 h-4 ${meta.color}`} />
              </div>
            )}
            <div>
              <div className="flex items-center gap-1.5">
                <p className="font-medium text-gray-900 dark:text-white">{v}</p>
                {row.featured && (
                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {row.category_name || row.category}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: "price",
      label: "Price",
      render: (v, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">
            {formatCurrency(v)}
          </p>
          {row.discount_percent > 0 && (
            <p className="text-xs text-green-600 dark:text-green-400">
              {row.discount_percent}% off →{" "}
              {formatCurrency(row.discounted_price)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "home_collection",
      label: "Badges",
      render: (v) => (
        <div className="flex flex-col gap-1">
          {v === 1 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <Home className="w-3 h-3" />
              Home
            </span>
          )}
        </div>
      ),
    },
    {
      key: "is_active",
      label: "Status",
      render: (v, row) => (
        <StatusToggleCell
          active={v === 1 || v === true}
          onToggle={() => handleToggleStatus(row)}
        />
      ),
    },
    {
      key: "id",
      label: "Actions",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setSelected(row);
              setModal("edit");
            }}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  /* Stats — ✅ CHANGE 5: pagination.total se total count */
  const activeTests = tests?.filter((t) => t.is_active === 1).length || 0;
  const featuredTests = tests?.filter((t) => t.featured).length || 0;
  const homeTests = tests?.filter((t) => t.home_collection === 1).length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Lab Tests
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage diagnostic lab tests and packages
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulk(true)}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload
          </button>
          <button
            onClick={() => {
              setSelected(null);
              setModal("add");
            }}
            className="btn-primary flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Add Lab Test
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          // ✅ CHANGE 6: Total me pagination.total use kiya
          {
            label: "Total Tests",
            value: pagination?.total || 0,
            color: "text-gray-900 dark:text-white",
            bg: "",
          },
          {
            label: "Active",
            value: activeTests,
            color: "text-green-700 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/10",
          },
          {
            label: "Featured",
            value: featuredTests,
            color: "text-yellow-600 dark:text-yellow-400",
            bg: "bg-yellow-50 dark:bg-yellow-900/10",
          },
          {
            label: "Home Collection",
            value: homeTests,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-900/10",
          },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* Price range filter */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
          Price Range:
        </span>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <input
            type="number"
            min="0"
            placeholder="Min"
            value={filters.priceMin}
            onChange={(e) => handleFilterChange("priceMin", e.target.value)} // ✅ CHANGE 7
            className="input-field py-1.5 text-sm w-24"
          />
          <span className="text-gray-400">—</span>
          <input
            type="number"
            min="0"
            placeholder="Max"
            value={filters.priceMax}
            onChange={(e) => handleFilterChange("priceMax", e.target.value)} // ✅ CHANGE 7
            className="input-field py-1.5 text-sm w-24"
          />
          {(filters.priceMin || filters.priceMax) && (
            <button
              onClick={() => {
                handleFilterChange("priceMin", "");
                handleFilterChange("priceMax", "");
              }}
              className="text-xs text-red-500 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* FilterBar */}
      <FilterBar
        filters={filters}
        onChange={handleFilterChange} // ✅ CHANGE 7
        onReset={handleReset} // ✅ CHANGE 7
        searchPlaceholder="Search by test name…"
        fields={filterFields}
        activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Lab Tests (${filtered.length} of ${pagination?.total || 0})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="lab-tests"
        serverPagination={true}
      />

      {/* ✅ CHANGE 8: Pagination UI */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between flex-wrap gap-3 px-1">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {(page - 1) * LIMIT + 1}–
              {Math.min(page * LIMIT, pagination.total)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {pagination.total}
            </span>{" "}
            tests
          </p>

          <div className="flex items-center gap-1.5">
            {/* Prev */}
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={!pagination.has_prev}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ← Prev
            </button>

            {/* Page numbers */}
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 ||
                  p === pagination.total_pages ||
                  Math.abs(p - page) <= 2,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`dots-${idx}`} className="px-2 text-gray-400">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 text-sm rounded-lg font-medium transition-colors
                      ${
                        page === p
                          ? "bg-blue-600 text-white"
                          : "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                      }`}
                  >
                    {p}
                  </button>
                ),
              )}

            {/* Next */}
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.has_next}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {showBulk && (
        <BulkUploadModal
          onClose={() => setShowBulk(false)}
          onSuccess={() => {
            refetch();
            setShowBulk(false);
          }}
        />
      )}

      {slotTarget && (
        <SlotManagerModal
          test={slotTarget}
          onClose={() => setSlotTarget(null)}
        />
      )}

      <Modal
        isOpen={modal === "add" || modal === "edit"}
        onClose={() => {
          setModal(null);
          setSelected(null);
        }}
        title={modal === "edit" ? "Edit Lab Test" : "Add Lab Test"}
        size="xl"
      >
        <LabTestForm
          initial={modal === "edit" ? selected : null}
          activeCategories={activeCats || []}
          activeSlots={activeSlots || []}
          allTests={tests.filter((t) => !selected || t.id !== selected.id)}
          onSubmit={handleSave}
          onClose={() => {
            setModal(null);
            setSelected(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default LabTests;
